<template>
    <div>
        <v-img eager :lazy-src="imgPlaceholder" :height="isMobile ? 100 : 250" :class="[{'img-profile': isMobile}]"
               :src="coachImg" :contain="noImg">
            <v-btn class="ma-1" icon @click="editAvatar">
                <v-icon color="black" class="border-icon">mdi-camera-outline</v-icon>
            </v-btn>
        </v-img>
        <v-container>

            <v-card style="top: -20px;" class="kimsa-elevation">
                <v-row class="text-center">
                    <v-col>
                        <v-icon color="accent">mdi-account-outline</v-icon>
                        <span class="detail-number">
						{{ activeStudents }}
					</span>
                        <div class="detail-description">
                            {{ $tc('student.student', 2) }}
                        </div>
                    </v-col>
                    <v-divider vertical inset></v-divider>
                    <v-col>
                        <v-icon color="accent">mdi-star-outline</v-icon>
                        <span class="detail-number">
						{{ coachStars.toFixed(1).replace('.0', '') }}
					</span>
                        <div class="detail-description">
                            {{ $t('coach.rating') }}
                        </div>
                    </v-col>
                </v-row>
            </v-card>

            <div class="coach-name mt-3">
                <div class="float-right mr-3 ml-n10">
                    <v-btn small icon @click="editBasicInfo">
                        <v-icon color="secondary">mdi-square-edit-outline</v-icon>
                    </v-btn>
                </div>
                {{ account.name || $t('common.no_name') }}
            </div>

            <v-divider class="divider-primary"></v-divider>

            <div class="coach-description pa-2">
                {{ account.description || $t('common.no_description') }}
            </div>

            <v-row no-gutters>

                <v-col cols="12" md="6" class="pa-2">
                    <v-row class="px-4 mt-4" align="center">
                        <div class="title-section">
                            {{ $t('coach.professional_info') }}
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn small icon @click="editProfessionalInfo">
                            <v-icon color="secondary">mdi-square-edit-outline</v-icon>
                        </v-btn>
                    </v-row>

                    <v-divider class="divider-primary"></v-divider>

                    <v-sheet class="mt-4 kimsa-elevation">
                        <v-container v-if="account.experience || account.professional_preparation">
                            <div class="experience" v-if="account.experience">
                                <b>{{ account.experience }}</b> {{ $t('coach.experience_years') }}
                            </div>

                            <div class="professional-preparation">
                                {{ account.professional_preparation }}
                            </div>
                        </v-container>
                        <v-container v-else>
                            <div class="caption">
                                {{ $t('common.no_data_yet') }}
                            </div>
                        </v-container>
                    </v-sheet>
                </v-col>

                <v-col cols="12" md="6" class="pa-2">
                    <v-row class="px-4 mt-4" align="center">
                        <div class="title-section">
                            {{ $t('coach.actual_plan') }}
                        </div>
                        <v-spacer></v-spacer>
                        <v-btn small icon @click="editPlan" v-if="showEditPlan">
                            <v-icon color="secondary">mdi-square-edit-outline</v-icon>
                        </v-btn>
                    </v-row>

                    <v-divider :class="['divider-primary', {'mt-5': !showEditPlan}]"></v-divider>

                    <template v-if="coachPlan">
                        <v-sheet class="mt-4 kimsa-elevation">
                            <v-container>
                                <v-row class="px-3">
                                    <div class="plan-name">
                                        {{ coachPlan.name }}
                                    </div>
                                    <v-spacer></v-spacer>
                                    <div class="plan-price">
                                        <span class="plan-currency">{{ coachPlan.currency }}</span>
                                        ${{ coachPlan.amount }}
                                    </div>
                                </v-row>
                            </v-container>
                        </v-sheet>
                        <v-row no-gutters>
                            <v-col cols="12">
                                <subscription-status class="mt-2 mb-0"/>
                            </v-col>
                        </v-row>
                    </template>
                    <v-sheet class="mt-4 kimsa-elevation" v-if="account.accountType === 'coach_company'">
                        <div class="caption pa-3">
                            Tu cuenta esta vinculada con una empresa.
                        </div>
                    </v-sheet>
                </v-col>

                <v-col cols="12" md="6" class="pa-2">
                    <v-row class="px-4 mt-4" align="center">
                        <div class="title-section">
                            {{ $t('coach.trained_with') }}
                        </div>
                        <v-spacer></v-spacer>
                        <div class="trained-students">
                            ({{ trainedStudents }})
                        </div>
                    </v-row>

                    <v-divider class="divider-primary mt-5"></v-divider>

                    <v-row v-if="account.lastStudents && account.lastStudents.length > 0">
                        <v-col cols="6" v-for="student in account.lastStudents" :key="student">
                            <student-mini-card :studentId="student"/>
                        </v-col>
                    </v-row>
                    <div v-else>
                        <v-alert color="accent" outlined dense icon="mdi-information-outline">
                            {{ $t('coach.without_students_trained') }}
                        </v-alert>
                    </div>
                </v-col>

            </v-row>

            <div style="height: 40px"></div>
            <v-btn :class="[{'delete-account': !isMobile}]" :block="isMobile" color="error" depressed outlined
                   @click="showDeleteAccount">
                <v-icon left>mdi-delete-outline</v-icon>
                {{ $t('common.delete_account') }}
            </v-btn>

            <upload-avatar-dialog ref="uploadPhoto" v-kim-dialog></upload-avatar-dialog>

            <kimsa-confirm
                ref="delete"
                :title="$t('common.delete_account_title')"
                :content="$t('common.delete_account_subtitle')"
                :loading="loadingDelete"
                :persistent="false"
                @confirm="onConfirmDelete"
                :close-on-confirm="false"
                :alert-mode="true"
            />

        </v-container>
    </div>
</template>

<script>

import StudentMiniCard from '@/components/coach/StudentMiniCard'
import UploadAvatarDialog from '@/components/student/UploadAvatarDialog'
import firebase, {db} from "@/utils/Firebase";
import SubscriptionStatus from "@/components/student/SubscriptionStatus";
import KimsaConfirm from "@/components/common/KimsaConfirm";

export default {
    data() {
        return {
            coachPlan: null,
            loadingDelete: false,
        }
    },
    components: {
        KimsaConfirm,
        SubscriptionStatus,
        StudentMiniCard,
        UploadAvatarDialog,
    },
    async mounted() {
        const vm = this
        await vm.getPlan()
    },
    computed: {
        coachImg(vm = this) {
            return vm.account && vm.account.avatar || vm.imgPlaceholder
        },
        activeStudents(vm = this) {
            return vm.account?.activeStudents || 0
        },
        coachStars(vm = this) {
            return vm.account?.rating?.stars || 0
        },
        currency() {
            return process.env.VUE_APP_CURRENCY
        },
        trainedStudents(vm = this) {
            if (!vm.account.trainedStudents) return '00'
            if (vm.account.trainedStudents < 10) return `0${vm.account.trainedStudents}`
            return vm.account.trainedStudents
        },
        noImg(vm = this) {
            return !vm.account?.avatar
        },
        showEditPlan(vm = this) {
            return !vm.isIphone && vm.account?.accountType === 'coach'
        }
    },
    methods: {
        editAvatar() {
            const vm = this
            vm.$refs.uploadPhoto.show()
        },
        editProfessionalInfo() {
            var vm = this
            vm.$router.push({name: 'coach.profile.edit', params: {mode: 'ProfessionalInfo'}})
        },
        editBasicInfo() {
            var vm = this
            vm.$router.push({name: 'coach.profile.edit', params: {mode: 'BasicInfoStepper'}})
        },
        editPlan() {
            var vm = this
            vm.$router.push({name: 'coach.profile.edit', params: {mode: 'SelectPlan'}})
        },
        async getPlan() {
            const vm = this
            let priceId = vm.account && vm.account.stripeAccount && vm.account.stripeAccount.currentSubscription &&
                vm.account.stripeAccount.currentSubscription.priceId || null
            if (!priceId) return
            let coachPlanRef = await db.collection('plans').where('priceId', '==', priceId).get()
            if (coachPlanRef.docs[0]) vm.coachPlan = coachPlanRef.docs[0].data()
        },
        showDeleteAccount() {
            const vm = this
            vm.$refs.delete.show()
        },
        onConfirmDelete() {
            const vm = this
            vm.loadingDelete = true
            vm.deleteAccount()
            setTimeout(() => {
                vm.$refs.delete.hide()
                vm.loadingDelete = false
                vm.$router.replace({name: 'login'})
            }, 3000)
        },
        deleteAccount: firebase.functions().httpsCallable('httpPayments-deleteAccount'),
    },
}
</script>

<style scoped>

.img-profile {
    position: relative;
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;
    max-width: 100vw;
}

.detail-number {
    color: var(--v-accent-base);
    font-weight: bold;
    margin-left: 2px;
    font-size: 17px;
}

.detail-description {
    font-size: 13px;
    color: #333333;
}

.v-divider--vertical.v-divider--inset {
    margin-bottom: 8px;
}

.coach-name {
    font-size: 25px;
    color: #333333;
    text-align: center;
}

.coach-description {
    font-size: 12px;
    color: #707070;
    text-align: center;
}

.divider-primary {
    height: 3px;
    max-height: 10px;
    background-color: var(--v-primary-base);
    margin-top: 10px;
    margin-bottom: 10px;
}

.title-section {
    font-size: 20px;
    color: #666666;
}

.experience {
    font-size: 15px;
    color: var(--v-accent-base);
}

.professional-preparation {
    font-size: 13px;
    color: #707070;
}

.plan-name {
    font-size: 15px;
    color: #707070;
}

.plan-currency {
    font-size: 17px;
    color: #707070;
}

.plan-price {
    font-size: 20px;
    color: #707070;
    font-weight: bold;
}

.trained-students {
    font-size: 17px;
    color: #707070;
}

.border-icon {
    background-color: rgba(255, 255, 255, 0.3);
    border-radius: 2px;
}

.delete-account {
    position: absolute;
    bottom: 20px;
    right: 30px;
}

</style>